import { capitalize } from 'lodash/fp';
import { DateTime } from 'luxon';

import {
  InquiryTemplateVersionStatus,
  PartialInquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import {
  WorkflowDeployment,
  WorkflowDeploymentConfigurationLabel,
} from 'dashboard/models/workflow-deployment';
import { WorkflowVersion, WorkflowVersionStatus } from 'dashboard/models/workflow-version';

export enum VersionState {
  Live = 'live',
  Saved = 'saved',
  Unsaved = 'unsaved',
  Published = 'published',
  Archived = 'archived',
}

export type VersionMetadata = {
  id: string;
  title: string;
  status: VersionState;
  editable: boolean;
  discardable: boolean;
  restorable: boolean;
  description?: string;
  'last-updater'?: string;
  'updated-at': DateTime | null;
  'published-at': DateTime | null;
  'edited-at': DateTime | null;
};

export const inquiryTemplateVersionMetadata = (
  inquiryTemplateVersions: PartialInquiryTemplateVersion[],
): VersionMetadata[] => {
  return inquiryTemplateVersions
    .map((inquiryTemplateVersion, i) => {
      const isLatest = i === inquiryTemplateVersions.length - 1;
      const inquiryTemplateStatus = inquiryTemplateVersion.attributes.status;
      const isDraft = inquiryTemplateStatus === InquiryTemplateVersionStatus.Draft;
      const versionState = inquiryTemplateVersion.attributes.live
        ? VersionState.Live
        : inquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published
          ? VersionState.Published
          : VersionState.Saved;

      return {
        id: inquiryTemplateVersion.id,
        title: capitalize(inquiryTemplateStatus),
        status: versionState,
        description: inquiryTemplateVersion.attributes.description,
        editable: isDraft,
        restorable: !isDraft && !isLatest,
        discardable: isDraft,
        'last-updater': inquiryTemplateVersion.attributes['last-updater'],
        'updated-at': inquiryTemplateVersion.attributes['updated-at']
          ? DateTime.fromISO(inquiryTemplateVersion.attributes['updated-at'])
          : null,
        'published-at': inquiryTemplateVersion.attributes['published-at']
          ? DateTime.fromISO(inquiryTemplateVersion.attributes['published-at'])
          : null,
        'edited-at': inquiryTemplateVersion.attributes['edited-at']
          ? DateTime.fromISO(inquiryTemplateVersion.attributes['edited-at'])
          : null,
      };
    })
    .reverse();
};

export const workflowVersionMetadata = (
  latestDraftVersionId: string,
  versions: (WorkflowVersion | WorkflowBlock)[],
  workflowDeployment?: WorkflowDeployment,
): VersionMetadata[] => {
  return versions
    .sort((a, b) => {
      const aTime = DateTime.fromISO(a.attributes['created-at']).toMillis();
      const bTime = DateTime.fromISO(b.attributes['created-at']).toMillis();
      return bTime - aTime;
    })
    .map((version, i) => {
      const isLatest = i === 0;
      const liveVersionIds = workflowDeployment?.attributes['configuration']?.versions
        ?.filter(
          (version) =>
            version.label === WorkflowDeploymentConfigurationLabel.Treatment &&
            version.percentage > 0,
        )
        .map((version) => version.token);

      const versionStatus = version.attributes.status;
      const versionState = liveVersionIds?.includes(version.id)
        ? VersionState.Live
        : versionStatus === WorkflowVersionStatus.Published
          ? VersionState.Published
          : versionStatus === WorkflowVersionStatus.Draft
            ? VersionState.Saved
            : VersionState.Archived;
      const isDraft = versionStatus === WorkflowVersionStatus.Draft;

      return {
        id: version.id,
        title: latestDraftVersionId === version.id ? 'Current' : capitalize(versionStatus),
        status: versionState,
        description: version.attributes.description,
        editable: latestDraftVersionId === version.id,
        restorable: !isLatest,
        discardable: isDraft && isLatest,
        'last-updater': version.attributes['last-updater'],
        // Even though workflow version has a 'updated-at' field, it is almost never
        // correct to surface it since it tend to be the date of the last rollplan performed
        // If neither published nor edited timestamp is available, we rather show nothing.
        // This should only happen with older workflow versions.
        'updated-at': null,
        'published-at': version.attributes['published-at']
          ? DateTime.fromISO(version.attributes['published-at'])
          : null,
        'edited-at': version.attributes['edited-at']
          ? DateTime.fromISO(version.attributes['edited-at'])
          : null,
      };
    });
};
